<template>
<div class="main list-view no-sub-header">

	<div v-if="showFilters" class="left-sidebar filters">

		<div class="title">Select which setting you want to edit...</div>

		<div :class="['filter-list-button-large', settingSet == 'TAX' ? 'active' : '']" @click="toggleSettings('TAX')">
			<div class="label">Sales Tax</div>
			<div class="note">Set how your online sales tax is calculated.</div>
		</div>
		<div :class="['filter-list-button-large', settingSet == 'SHIPPING'? 'active' : '']" @click="toggleSettings('SHIPPING')">
			<div class="label">Shipping</div>
			<div class="note">Set your shipping and pickup preferences.</div>
		</div>
		<div :class="['filter-list-button-large', settingSet == 'PAYMENTS'? 'active' : '']" @click="toggleSettings('PAYMENTS')">
			<div class="label">Payments</div>
			<div class="note">Gift card and other payment settings.</div>
		</div>
		<div :class="['filter-list-button-large', settingSet == 'NOTIFICATIONS'? 'active' : '']" @click="toggleSettings('NOTIFICATIONS')">
			<div class="label">Notifications</div>
			<div class="note">Set notifications and messages.</div>
		</div>
		<div :class="['filter-list-button-large', settingSet == 'REQUIRED'? 'active' : '']" @click="toggleSettings('REQUIRED')">
			<div class="label">Required Info</div>
			<div class="note">Set customer info required at checkout.</div>
		</div>

	</div>

	<div class="detail">

		<div class="main-content list-detail" v-if="!loading">
			<h1>{{ settingTitles[settingSet] }}</h1>

			<div v-if="settingSet == 'TAX'">
				<div class="two-columns settings-columns">
					<div class="settings-group">
						<label>Your Store's Sales Tax Rate</label>
						<number-input :decimals='5' v-model="settings.tax_rate" :key="'tax_rate_' + settings.tax_rate" />
					</div>
					<div class="instruction">Enter as decimal, e.g. 0.065 = 6.5%</div>
				</div>
				<div class="two-columns settings-columns">
					<div class="settings-group">
						<label class="full">Which shipped web sales are charged sales tax?</label>
						<label class="radio first">
							<input :disabled="noAdvancedSettings" type="radio" v-model="settings.tax_orders" value="in-state">
							<span :class="{ disabled: noAdvancedSettings }"><i class="fas fa-check-circle"></i>In-state web sales only</span>
						</label>
						<label class="radio last">
							<input :disabled="noAdvancedSettings" type="radio" v-model="settings.tax_orders" value="all">
							<span :class="{ disabled: noAdvancedSettings }"><i class="fas fa-check-circle"></i>All web sales (in-state and out-of-state)</span>
						</label>
					</div>
					<div class="instruction">
						All <b>in-store pickup</b> web orders will be charged sales tax using your store's sales tax rate.<br />
						You can set which <b>shipped</b> web orders should also be charged sales tax.
					</div>
				</div>
				<div class="two-columns settings-columns">
					<div class="settings-group">
						<label class="full">What is the sales tax basis for shipped web sales?</label>
						<label class="radio first">
							<input :disabled="noAdvancedSettings" type="radio" v-model="settings.tax_basis" value="store">
							<span :class="{ disabled: noAdvancedSettings }"><i class="fas fa-check-circle"></i>Your location's sales tax rate</span>
						</label>
						<label class="radio last">
							<input :disabled="noAdvancedSettings" type="radio" v-model="settings.tax_basis" value="customer">
							<span :class="{ disabled: noAdvancedSettings }"><i class="fas fa-check-circle"></i>Your customer's shipping address</span>
						</label>
						<p v-if="noAdvancedSettings">
							<i class="fas fa-ban"></i> These settings are only available when using one of our new website templates.
						</p>
					</div>
					<div class="instruction">
						You can set the tax basis for <b>shipped</b> web orders to use either your store's tax rate or the tax authority of your customer's shipping address.
					</div>
				</div>
			</div>
			<div v-if="settingSet == 'SHIPPING'">
				<div class="two-columns settings-columns">
					<div class="settings-group">
						<label>In-Store Pickup</label>
						<number-input :decimals='2' v-model="settings.flat_pickup_fee" :key="'pickup_fee_' + settings.flat_pickup_fee" />
					</div>
					<div class="instruction">Shown as "Handling" at checkout.</div>
				</div>
				<div class="two-columns settings-columns">
					<div class="settings-group">
						<label>Firearms In-Store Pickup</label>
						<number-input :decimals='2' v-model="settings.flat_pickup_fee_firearms" :key="'pickup_fee_firearms_' + settings.flat_pickup_fee_firearms" />
					</div>
					<div class="instruction">Shown as "Handling" at checkout with firearms.</div>
				</div>
				<div class="two-columns settings-columns">
					<div class="settings-group">
						<label>Cart Handling Fee</label>
						<number-input :decimals='2' v-model="settings.cart_handling_fee" :key="'cart_handling_' + settings.flat_pickup_fee_firearms" />
					</div>
					<div class="instruction">Applied to cart excluding non-inventory items.</div>
				</div>
				<div class="two-columns settings-columns">
					<div class="settings-group">
						<label class="full">Flat Shipping</label>
						<number-input :decimals='2' :inline="true" v-model="settings.flat_shipping_fee" :key="'flat_shipping_' + settings.flat_shipping_fee" />
						<input type="checkbox" v-model="settings.flat_shipping_per_item" :key="'flat_shipping_per_item_' + settings.flat_shipping_per_item"></input> Applies to each item in cart
					</div>
					<div class="instruction">Shown as "Shipping" at checkout. Must be greater than zero.<br>NOTE: leave blank if you prefer to use standard shipping calculation.</div>
				</div>
				<div class="two-columns settings-columns">
					<div class="settings-group">
						<label>Local Delivery Fee</label>
						<number-input :decimals='2' v-model="settings.local_delivery_fee" :key="'local_delivery_' + settings.local_delivery_fee" />
					</div>
					<div class="instruction">If set, enables a local delivery option on your checkout page.</div>
				</div>
				<div class="two-columns settings-columns">
					<div class="settings-group">
						<label class="full">Firearms Flat Shipping</label>
						<number-input :decimals='2' :inline="true" v-model="settings.flat_shipping_fee_firearms" :key="'shipping_firearms_' + settings.flat_shipping_fee_firearms" />
						<input type="checkbox" v-model="settings.flat_shipping_per_firearm" :key="'flat_shipping_per_firearm_' + settings.flat_shipping_per_firearm"></input> Applies to each firearm in cart
					</div>
					<div class="instruction">Shown as "Shipping" at checkout with firearms. Must be greater than zero.<br>NOTE: leave blank if you prefer to use standard shipping calculation.</div>
				</div>
				<div class="two-columns settings-columns">
					<div class="settings-group">
						<label>Firearms Fee</label>
						<number-input :decimals='2' v-model="settings.firearms_handling_fee" :key="'handling_firearms_' + settings.firearms_handling_fee" />
					</div>
					<div class="instruction">Applied per firearm in order.</div>
				</div>
				<div class="two-columns settings-columns">
					<div class="settings-group">
						<label>Hazmat Fee</label>
						<number-input :decimals='2' v-model="settings.hazmat_fee" :key="'hazmat_fee_' + settings.hazmat_fee" />
					</div>
					<div class="instruction">Applied if primers / gunpowder in order.</div>
				</div>
				<div class="two-columns settings-columns">
					<div class="settings-group">
						<label>Preferred Shipper</label>
						<div class="control-group">
	    					<label class="radio first">
	    						<input type="radio" v-model="settings.shipping_carrier" value="fedex">
	    						<span><i class="fas fa-check-circle"></i>FedEx</span>
	    					</label>
	    					<label class="radio">
	    						<input type="radio" v-model="settings.shipping_carrier" value="ups">
	    						<span><i class="fas fa-check-circle"></i>UPS</span>
	    					</label>
	    					<label class="radio last">
	    						<input type="radio" v-model="settings.shipping_carrier" value="least_expensive">
	    						<span><i class="fas fa-check-circle"></i>Least Expensive</span>
	    					</label>
						</div>
					</div>
					<div class="instruction">Least expensive will compare FedEx and UPS rates at checkout.</div>
				</div>
				<div class="two-columns settings-columns">
					<label class="checkbox with-outside-label">
						<input type="checkbox" v-model="settings.show_ffl_finder">
						<span><i class="fas fa-check-circle"></i></span>
						<span class="label-text">Show FFL Finder link with dealer shipping info</span>
					</label>
					<div class="settings-group">
						<label>FFL Finder URL</label>
						<input :disabled="!settings.show_ffl_finder" type="text" v-model="settings.ffl_finder_url" placeholder="https://www.example.com">
					</div>
					<div class="instruction">Requires a full URL to work properly - ex. https://www.example.com</div>
				</div>
				<div class="two-columns settings-columns">
					<div class="settings-group">
						<label>Canada Export Fee</label>
						<input type="text" v-model="settings.international_shipping_fee">
					</div>
					<div class="instruction">Enter a fee here to enable Canadian orders on your checkout page.</div>
					<div class="settings-group">
						<label>Canada Firearms Export Fee</label>
						<input type="text" v-model="settings.international_firearms_shipping_fee">
					</div>
					<div class="instruction">Enter an optional per-firearm fee for Canadian orders.</div>
				</div>
				<div class="two-columns settings-columns">
					<div>
						<label class="checkbox with-outside-label" :key="'require_shipping_billing_match_' + settings.require_shipping_billing_match">
							<input type="checkbox" v-model="settings.require_shipping_billing_match" :disabled="settings.disable_ship_to_customer != ''">
							<span><i class="fas fa-check-circle"></i></span>
							<span class="label-text">Require shipping and billing address match for drop-ship <i>(helps prevent fraud)</i></span>
						</label><br>
						<label class="checkbox with-outside-label" :key="'force_product_restrictions' + settings.force_product_restrictions">
							<input type="checkbox" v-model="settings.force_product_restrictions">
							<span><i class="fas fa-check-circle"></i></span>
							<span class="label-text">Enforce state restrictions for products at checkout <i>(restrictions are set by distributors)</i></span>
						</label>
						<div class="two-columns v-spaced">
							<div>
								<label>Delivery options</label><br>
								<input type="radio" v-model="settings.delivery_option" value="pickup"> Pickup In Store Only<br>
								<input type="radio" v-model="settings.delivery_option" value="ship"> Ship To Customer Only<br>
								<input type="radio" v-model="settings.delivery_option" value="none"> Customer can choose either Pickup or Ship To
							</div>
							<div v-if="settings.delivery_option == 'none'">
								<label>Default delivery option</label><br>
								<input type="radio" v-model="settings.default_to_ship" value="ship"> Ship To Customer<br>
								<input type="radio" v-model="settings.default_to_ship" value=""> Pickup In Store
							</div>
						</div>
					</div>
				</div>
				<div class="two-columns settings-columns">
					<div class="settings-group">
						<label class="full">Pickup Options <i>(list each location on a separate line)</i></label>
						<textarea type="text" v-model="settings.pickup_options" style="width: 500px; height: 80px;" placeholder="Use if you have more than one location for order pickup"></textarea>
					</div>
					<div class="instruction">Enter each pickup location option on a single line.</div>
				</div>

			</div>

			<div v-if="settingSet == 'PAYMENTS'">

				<div class="settings-columns">
					<p>Looking for your credit card processor settings? Go to <router-link to="/merchant-settings"><b>Merchant Settings</b></router-link>.</p>
				</div>
				<div class="two-columns settings-columns">
					<label class="checkbox with-outside-label">
						<input type="checkbox" v-model="settings.disable_gift_cards">
						<span><i class="fas fa-check-circle"></i></span>
						<span class="label-text">Disable gift cards at <b>Checkout</b></span>
					</label>
					<div class="instruction">Gift cards payments will not be shown for checkout.<br>(Need to disable for POS? <router-link to="/pos/settings/payments">Use POS Settings</router-link>.)</div>
				</div>

				<div v-if="settings.credova_enabled" class="two-columns settings-columns">
					<div class="settings-group">
						<label>Credova Store ID</label>
						<input type="text" v-model="settings.credova_store_id">
					</div>
					<div class="instruction">Your Credova Store ID can be found in your Credova Dashboard.<br>Pay close attention to how your store id is caplitalized.</div>

					<div class="settings-group">
						<label>Credova Key</label>
						<input type="text" :disabled="noAdvancedSettings" v-model="settings.credova_password">
					</div>
					<div class="instruction">
						Your Credova key is required to enble Credova at checkout.<br>
						It should look like z@_Mrz31B@FN@-E$9V@wC5_9a0XJWn...
					</div>
					<p v-if="noAdvancedSettings">
						<i class="fas fa-ban"></i> These settings are only available when using one of our new website templates.
					</p>
				</div>

			</div>

			<div v-if="settingSet == 'NOTIFICATIONS'">
				<div class="two-columns settings-columns">
					<div class="settings-group">
						<label>Order Notifications</label>
						<textarea v-model="settings.notifications" style="height: 5em;"></textarea>
					</div>
					<div class="instruction">Enter one or more email addresses that will receive notification of online orders.<br><br>If entering more than one, put each address on a separate line.</div>
				</div>
				<div class="two-columns settings-columns">
					<label class="checkbox with-outside-label" :key="'new_account_notification_' + settings.new_account_notification">
						<input type="checkbox" v-model="settings.new_account_notification">
						<span><i class="fas fa-check-circle"></i></span>
						<span class="label-text">Receive a notification when a customer creates an account.</span>
					</label>
					<div class="instruction">Notifications will be sent to your Store Profile email address.</div>
				</div>
				<div class="two-columns settings-columns">
					<div class="settings-group">
						<label>Minimum Checkout Age</label>
						<number-input :decimals='0' v-model="settings.checkout_minimum_age" :key="'checkout_minimum_age_' + settings.checkout_minimum_age" />
					</div>
					<div class="instruction">Customers must check they are minimum age.</div>
					<label class="checkbox with-outside-label" :key="'checkout_agree_policies_' + settings.checkout_agree_policies">
						<input type="checkbox" v-model="settings.checkout_agree_policies">
						<span><i class="fas fa-check-circle"></i></span>
						<span class="label-text">Customers must agree to store policies at checkout.</span>
					</label>
				</div>
				<div class="two-columns settings-columns">
					<div class="settings-group">
						<label>Checkout Message</label>
						<textarea v-model="settings.checkout_message" style="height: 5em;"></textarea>
					</div>
					<div class="instruction">Enter an optional message to display to your customers on the checkout page.</div>

					<div class="settings-group">
						<label>Checkout Complete Message</label>
						<textarea v-model="settings.order_confirmation_message" style="height: 5em;"></textarea>
					</div>
					<div class="instruction">Enter an optional message to display to your customers on the order complete page.</div>
				</div>
				<div class="two-columns settings-columns">
					<div class="spacer">Customer Order Email</div>
					<div class="settings-group">
						<label>Pick Up Message</label>
						<textarea v-model="settings.order_email_pickup_message" placeholder="We will contact you when your order is available for in-store pickup." maxlength="300" style="height: 5em;"></textarea>
					</div>
					<div class="instruction">
						<div>Add an additional pick up message for your customer's order confirmation email and checkout. The message will always state "Hold for pickup" at store location and this extra message added to it. The default appended message is "We will contact you when your order is available for in-store pickup.""</div>
						<div class="spacer"></div>
						<div>Maximum 300 characters allowed</div>
					</div>
				</div>
			</div>

			<div v-if="settingSet == 'REQUIRED'">
				<div class="two-columns settings-columns">
					<div class="settings-group two-columns" style="grid-template-columns: 1fr 1fr 5em; align-items: bottom;">
						<div>
							<label>Custom Field</label>
							<select v-model="addCustomField.custom_field_id" :disabled="noAdvancedSettings">
								<option value="">Select...</option>
								<option v-for="(customField, index) in settings.custom_fields" :value="customField.custom_field_id">{{ customField.description }}</option>
							</select>
						</div>
						<div>
							<label>Product Type</label>
							<select v-model="addCustomField.product_type_id" :disabled="noAdvancedSettings">
								<option value="">Select...</option>
								<option v-for="(productType, index) in settings.product_types" :value="productType.product_type_id">{{ productType.description }}</option>
							</select>
						</div>
						<div>
							<label><br></label>
							<div :class="['button', addCustomField.custom_field_id ? 'action' : 'disabled']" @click="doAddCustomField()"><i class="fas fa-plus"></i></div>
						</div>
					</div>
					<div class="instruction">
						<p>Select a custom field to require for specific product types at customer checkout.</p>
						<p v-if="noAdvancedSettings">
							<i class="fas fa-ban"></i> These settings are only available when using one of our new website templates.
						</p>
					</div>
				</div>
				<div class="two-columns settings-columns">
					<div class="settings-group">
						<table class="data-list">
							<thead>
								<tr>
									<th class="list">Custom Field</th>
									<th class="list">Product Type</th>
									<th class="list center">Required</th>
									<th class="list right"></th>
								</tr>
							</thead>
							<tbody>
								<tr v-for="(customField, index) in settings.custom_field_groups">
									<td class="list">{{ customField.custom_field }}</td>
									<td class="list">{{ customField.product_type || 'Any Product Type' }}</td>
									<td class="list center">
										<input type="checkbox" v-model="customField.required" />
									</td>
									<td class="list right"><div class="button delete" @click="doRemoveCustomField(customField)"><i class="fas fa-times"></i></div></td>
								</tr>
							</tbody>
						</table>
					</div>
					<div v-if="settings.custom_field_groups.length > 0 && getClient.client_type_code == 'CDS'" class="instruction">
						<p>Would you like to duplicate these settings for POS?</p>
						<div class="button neutral" @click="duplciateCustomFieldSettings()">Duplciate</div>
						<div class="message">{{ duplicateMessage }}</div>
					</div>
				</div>
			</div>

			<div class="controls detail-controls" >
				<button class="button primary" @click="saveSettings()"><span><i class="fa fa-check"></i> Save {{ settingTitles[settingSet] }}</span></button>
			</div>

		</div>

        <loading v-else></loading>
	</div>

	<message-modal v-if="message" :message="message" :loading="saving" :errorStatus="messageStatus" :timeout="messageTimeout" @dismiss="message = ''" />

	<help-popup :helpLink="{76:'Checkout Settings', 77:'Sales Tax', 78:'Shipping', 79:'Payments', 338:'Credova Financing', 80:'Notifications', 364:'Website Required Info'}" :tours="false"></help-popup>

</div>
</template>

<script>
import NumberInput from '@/components/NumberInput.vue'
import HelpPopup from '@/components/HelpPopup.vue'

export default {
	name: 'settings',
	props: ['setting'],
    components: {
		'number-input': NumberInput,
		'help-popup': HelpPopup
	},
	data() {
		return {
			loading: false,
			showFilters: true,
			settingTitles: {
				'TAX': 'Sales Tax',
				'SHIPPING': 'Shipping Preferences',
				'PAYMENTS': 'Payment Settings',
				'NOTIFICATIONS': 'Notifications & Messages',
				'REQUIRED': 'Required Info'
			},
			addCustomField: {
				custom_field_id: '',
				product_type_id: ''
			},
			settingSet: '',
			settings: {},
			paymentProcessors: [],
			message: '',
			saving: false,
			messageStatus: '',
			messageTimeout: 0,
			duplicateMessage: '',
			clientId: '',
			templateTypeCode: ''
		}
	},
	created() {
		this.settingSet = this.setting ? this.setting.toUpperCase() : 'TAX'
		this.getSettings()
	},
	computed: {
		paymentProcessorCode() {
			if (!this.settings.payment_processors || !this.settings.payment_processor_id) { return '' }
			return this.settings.payment_processors.filter(obj => obj.payment_processor_id == this.settings.payment_processor_id)[0]['payment_processor_code']
		},
		paymentProcessorName() {
			if (!this.settings.payment_processors || !this.settings.payment_processor_id) { return '' }
			return this.settings.payment_processors.filter(obj => obj.payment_processor_id == this.settings.payment_processor_id)[0]['description']
		},
		noAdvancedSettings() {
			return this.templateTypeCode != 'WEBSITE_BUILDER'
		}
	},
    watch: {
        setting(newVal, oldVal) {
            this.settingSet = this.setting.toUpperCase()
            if (newVal != oldVal) {
                this.getSettings()
            }
		},
		"settings.default_to_ship"(newVal) {
			if (newVal === true) this.settings.disable_ship_to_customer = false
		},
		"settings.disable_store_pickup"(newVal) {
			if (newVal === true) this.settings.disable_ship_to_customer = false
		},
		"settings.disable_ship_to_customer"(newVal) {
			if (newVal === true) { this.settings.disable_store_pickup = false; this.settings.require_shipping_billing_match = false; this.settings.default_to_ship = false }
		},
  		'settings.order_email_pickup_message'(newVal) {
    		if (newVal.trim().length === 0) {
				this.settings.order_email_pickup_message = '';
    		}
		}
	},
	methods: {
		getSettings() {
            this.loading = true
			this.settings = {}
			let apiAction = this.settingSet == 'REQUIRED' ? 'get_item_custom_field_groups' : 'get_item_website_settings'
			this.apiCall({ destination: apiAction, data: { section: this.settingSet } }).then(obj => {
                this.loading = false
				this.settings = obj.settings
				// set string "0" as numeric zero and set to falsey when/if applied to toggle inputs
				for (let key in this.settings) {
  					if (this.settings[key] === "0") {
    					this.settings[key] = 0;
  					}
				}
				if (obj.template_type_code) {
					this.templateTypeCode = obj.template_type_code
				}
			}).catch(err => {
                this.loading = false
				console.log(err)
			})
		},
		toggleSettings(which) {
			if (which != this.settingSet) {
				this.$router.push('/website/checkout/' + which.toLowerCase())
			}
		},
		doAddCustomField() {
			if (!this.addCustomField.custom_field_id && !this.addCustomField.product_type_id) { return }
			let alreadyInList = false
			this.settings.custom_field_groups.forEach((element, index) => {
				if (element.custom_field_id == this.addCustomField.custom_field_id && element.product_type_id == this.addCustomField.product_type_id) {
					alreadyInList = true
					return
				}
			})
			if (alreadyInList) {
				return
			}
			let addObject = {}
			this.settings.custom_fields.forEach((element, index) => {
				if (element.custom_field_id == this.addCustomField.custom_field_id) {
					addObject.custom_field_id = element.custom_field_id
					addObject.custom_field = element.description
				}
		 	})
			this.settings.product_types.forEach((element, index) => {
				if (element.product_type_id == this.addCustomField.product_type_id) {
					addObject.product_type_id = element.product_type_id
					addObject.product_type = element.description
				}
		 	})
			addObject.required = 1
			this.settings.custom_field_groups.push(addObject)
			if (!addObject.product_type_id) {
				// if adding without a specific product_type, are there any for this field in the list with a product_type?
				let removeIndexes = []
				this.settings.custom_field_groups.forEach((element, index) => {
					if (element.custom_field_id == addObject.custom_field_id && element.product_type_id) {
						removeIndexes.push(index)
					}
			 	})
				if (removeIndexes.length > 0) {
					removeIndexes.reverse()
					removeIndexes.forEach((elementIndex) => {
						this.settings.custom_field_groups.splice(elementIndex, 1)
					})
				}
			} else {
				// we are adding a specific product type, check if there is a non-specific entry
				this.settings.custom_field_groups.forEach((element, index) => {
					if (element.custom_field_id == addObject.custom_field_id && !element.product_type_id) {
						this.settings.custom_field_groups.splice(index, 1)
					}
			 	})
			}
		},
		doRemoveCustomField(customField) {
			let removeIndex = -1
			this.settings.custom_field_groups.forEach((element, index) => {
				if (element.custom_field_id == customField.custom_field_id && element.product_type_id == customField.product_type_id) {
					removeIndex = index
					return
				}
			})
			if (removeIndex > -1) {
				this.settings.custom_field_groups.splice(removeIndex, 1)
			}
		},
		duplciateCustomFieldSettings() {
			this.duplicateMessage = ''
			this.apiCall({ destination: 'save_item_custom_field_groups', data: { force_section: 'POS', settings: this.settings } }).then(obj => {
				this.duplicateMessage = obj.message
				setTimeout(() => { this.duplicateMessage = '' }, 2000)
			}).catch(err => {
				console.log(err)
			})
		},
		saveSettings() {
			if (this.saving) { return }
			this.message = 'Saving checkout settings...'
			this.messageTimeout = 0
			this.messageStatus = ''
			this.saving = true
			let apiAction = this.settingSet == 'REQUIRED' ? 'save_item_custom_field_groups' : 'save_item_website_settings'
			this.apiCall({ destination: apiAction, data: { section: this.settingSet, settings: this.settings } }).then(obj => {
				this.saving = false
				this.messageStatus = obj.status
				if (this.messageStatus == 'success') {
					this.message = 'Checkout settings saved.'
					this.messageTimeout = 2000
				} else {
					this.message = obj.message
				}
			}).catch(err => {
				console.log(err)
			})
		}
	}
}
</script>

<style lang="scss" scoped>
.settings-columns {
	align-items: center;
	grid-template-columns: 2fr 1fr;
	border-bottom: 1px dotted #ccc;
	margin: 0 0 1em 0;
}
.settings-columns:last-of-type {
	border-bottom: 0 none;
}
.settings-group {
	grid-column: 1 / 2;
}
label.checkbox {
	grid-column: 1 / 2;
}
.label-text.disabled {
	background: initial;
	color: #CCC;
}
.spacer {
	margin-bottom: 1em;
}
</style>
